// Contratos del módulo "Material de clase": el profe sube archivos que
// documentan lo que efectivamente enseñó en el aula (PPT/Word/PDF/TXT/imagen).
// La IA extrae texto + resume en un JSON estructurado → alimenta el matrix
// generator y el interview engine para que las preguntas se centren en lo
// que se enseñó, no en el OA genérico.
import { z } from 'zod';
export const LESSON_MATERIAL_MIMES = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain',
    'image/jpeg',
    'image/png',
    'image/webp',
];
export const LessonMaterialMimeSchema = z.enum(LESSON_MATERIAL_MIMES);
// 20 MB por archivo (los remedial usan mismo cap). Suficiente para PDFs
// escaneados de una clase entera.
export const LESSON_MATERIAL_MAX_BYTES = 20 * 1024 * 1024;
// Hasta 5 archivos por material (PPT + guía + apunte + 2 fotos de pizarra).
export const LESSON_MATERIAL_MAX_FILES = 5;
// Cap de texto extraído concatenado. ~50 páginas — más excede el context
// window del summarizer con buffer para el prompt.
export const LESSON_MATERIAL_MAX_EXTRACTED_CHARS = 200_000;
// ── POST /v1/lesson-material/upload-url ──────────────────────────────────
export const LessonMaterialUploadUrlRequestSchema = z.object({
    mime: LessonMaterialMimeSchema,
    size_bytes: z
        .number()
        .int()
        .positive()
        .max(LESSON_MATERIAL_MAX_BYTES),
    filename: z.string().min(1).max(200),
});
export const LessonMaterialUploadUrlResponseSchema = z.object({
    upload_url: z.string().url(),
    blob_ref: z.string(),
    expires_at: z.string().datetime(),
});
// ── POST /v1/lesson-material/preview ─────────────────────────────────────
// Extrae texto + resume TODOS los archivos subidos. No persiste — el summary
// vive en memoria del SPA hasta que se cree el aula/pilot y se attache.
export const LessonMaterialFileRefSchema = z.object({
    blob_ref: z.string(),
    mime: LessonMaterialMimeSchema,
    filename: z.string().min(1).max(200),
    size_bytes: z.number().int().positive(),
});
export const LessonMaterialPreviewRequestSchema = z.object({
    files: z
        .array(LessonMaterialFileRefSchema)
        .min(1)
        .max(LESSON_MATERIAL_MAX_FILES),
    // Contexto para el summarizer — sin OA/hito el summary sale muy genérico.
    oa_codigo: z.string().min(1).max(80),
    oa_descripcion: z.string().min(1).max(2000),
    nivel_academico: z.string().min(1).max(80),
    asignatura: z.string().min(1).max(80),
});
// El schema que el LLM devuelve — también es lo que consume matrix/interview.
// IMPORTANTE: Azure structured outputs con strict:true requiere que todos los
// fields sean required y no soporta bien `.min()` sobre arrays anidados. Las
// restricciones de longitud viven en el prompt, no en el schema.
export const LessonMaterialSummarySchema = z.object({
    // Lo priorizado por el profe. Frases cortas ("mezcla de colores primarios y
    // secundarios") — NO explicaciones largas. El interview engine las usa como
    // pistas de qué preguntar.
    temas_priorizados: z.array(z.string()),
    // Términos exactos que el profe usó (aunque no sean canónicos MINEDUC).
    // El coach los reusa para hablar el mismo idioma que la clase.
    terminos_clave: z.array(z.string()),
    // Ejemplos CONCRETOS mencionados en el material. El coach puede pedirle al
    // alumno explicar "el ejemplo de X que vimos en clase" en vez de inventar.
    ejemplos_dados: z.array(z.string()),
    // Tipos de ejercicios trabajados. Guía al matrix generator sobre qué
    // formato de banco_preguntas producir.
    ejercicios_tipo: z.array(z.string()),
    // Nota del summarizer al profe: cosas que no logró extraer o dudas. String
    // vacío cuando no hay nada — no nullable porque Azure strict lo rechaza en
    // combinación con otros optional/nullable en el schema.
    notas: z.string(),
});
export const LessonMaterialPreviewResponseSchema = z.object({
    summary: LessonMaterialSummarySchema,
    extracted_text_len: z.number().int().nonnegative(),
    extractors_used: z.array(z.string()),
    llm: z.object({
        model: z.string(),
        prompt_tokens: z.number().int().nonnegative(),
        completion_tokens: z.number().int().nonnegative(),
        latency_ms: z.number().int().nonnegative(),
    }),
    // El SPA guarda esto y lo devuelve en /v1/classrooms o /v1/pilots.
    attach_ref: z.object({
        files: z.array(LessonMaterialFileRefSchema),
        summary: LessonMaterialSummarySchema,
        extracted_text_hash: z.string(),
    }),
});
// ── Attach payload — parte del body de POST /v1/classrooms|pilots ────────
// Wrappea el resultado del /preview + permite al profe editar el summary.
export const LessonMaterialAttachSchema = z.object({
    files: z
        .array(LessonMaterialFileRefSchema)
        .min(1)
        .max(LESSON_MATERIAL_MAX_FILES),
    summary: LessonMaterialSummarySchema,
    extracted_text_hash: z.string(),
});
//# sourceMappingURL=lesson-material.js.map